/** Ideas from the Pack, HQ (Randy and Jeff only): words and pure rules for the admin page. */

export type Board = "pro" | "family";
export type Role = "owner" | "team" | "family";
export type Status = "heard" | "planned" | "building" | "shipped" | "already_in_2oodles";
export type VoiceKind = "suggestion" | "matters" | "build" | "concern" | "vote";

export const BOARDS: readonly (readonly [Board, string])[] = [
  ["family", "Family board"],
  ["pro", "Pro board"],
];

export const STATUS_LABEL: Record<Status, string> = {
  heard: "Heard",
  planned: "Planned",
  building: "Building",
  shipped: "Shipped",
  already_in_2oodles: "Already in 2oodles",
};

export const KIND_LABEL: Record<VoiceKind, string> = {
  suggestion: "Suggested",
  matters: "This matters to me",
  build: "Build on it",
  concern: "Concern",
  vote: "Voted",
};

export const ROLE_LABEL: Record<Role, string> = { owner: "Owner", team: "Team member", family: "Family" };

/** HQ can filter who said what by role: on the Pro board Owners or Team members. */
export function roleFilters(board: Board): (readonly [Role | null, string])[] {
  return board === "pro"
    ? [[null, "Everyone"], ["owner", "Owners"], ["team", "Team members"]]
    : [[null, "Everyone"], ["family", "Families"]];
}

export const FIT_LABEL: Record<number, string> = {
  [-1]: "Weaker fit",
  0: "Neutral",
  1: "Fits",
  2: "Strong fit",
};

/** "week of Oct 26" from an ISO date (no time zone shift). */
export function weekWords(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  const month = new Date(Date.UTC(y, m - 1, d)).toLocaleString("en-US", { month: "short", timeZone: "UTC" });
  return `week of ${month} ${d}`;
}

export function weekLine(status: "proposed" | "draft" | "sent", picks: number): string {
  if (status === "sent") return "Sent";
  if (!picks) return "Nothing is waiting yet";
  return status === "draft" ? `${picks} picked · yours to send` : `2oodles picked ${picks} · waiting for you`;
}

export function n(value: number): string {
  return value.toLocaleString("en-US");
}

// --- phase E1: closing the loop ---------------------------------------------------------------

/** HQ's tabs: the two boards, then Ready to build and the Nailed-it score. */
export type Tab = Board | "work" | "score" | "problems" | "already";
export function tabs(workCount?: number | null, problemCount?: number | null, alreadyCount?: number | null): (readonly [Tab, string])[] {
  return [
    ...BOARDS,
    ["work", workCount ? `Ready to build · ${n(workCount)}` : "Ready to build"],
    ["score", "Nailed-it score"],
    // Phase E2: help without a help desk.
    ["problems", problemCount ? `Problems · ${n(problemCount)}` : "Problems"],
    ["already", alreadyCount ? `Already in 2oodles · ${n(alreadyCount)}` : "Already in 2oodles"],
  ];
}

export const SEVERITY_LABEL: Record<"urgent" | "high" | "normal" | "low", string> = {
  urgent: "Urgent", high: "High", normal: "Normal", low: "Low",
};
export const SEVERITY_CLASS: Record<"urgent" | "high" | "normal" | "low", string> = {
  urgent: "bg-red-100 text-red-800", high: "bg-amber-100 text-amber-900", normal: "bg-zinc-100 text-zinc-700", low: "bg-zinc-50 text-zinc-500",
};
export function peopleWords(people: number, reports: number): string {
  return `${n(people)} ${people === 1 ? "person" : "people"}${reports > people ? ` · ${n(reports)} reports` : ""}`;
}

export const WORK_KIND: Record<"build" | "another_pass" | "follow_up" | "easier_to_find", string> = {
  build: "Ready to build",
  another_pass: "Another pass",
  follow_up: "Small follow-up",
  easier_to_find: "Make it easier to find",
};

/** Which of the tier chips is lit: the highest the Nailed it % reaches (labels "Good 75%+"). */
export function tierIndex(nailedPct: number | null | undefined, tiers: readonly string[]): number {
  if (nailedPct == null) return -1;
  let lit = -1;
  tiers.forEach((label, i) => {
    const m = /(\d+)%\+/.exec(label);
    if (m && nailedPct >= Number(m[1])) lit = i;
  });
  return lit;
}

/** The numbers HQ can change, with their words (one place, phase E1). */
export const SETTING_FIELDS: readonly (readonly [string, string, number, number])[] = [
  ["nail_share", "Nailed it closes at (%)", 50, 100],
  ["min_answers", "…of at least (answers)", 1, 1000],
  ["tier_good", "Good from (%)", 1, 100],
  ["tier_excellent", "Excellent from (%)", 1, 100],
  ["tier_world", "World-class from (%)", 1, 100],
  ["industry_pct", "Industry average (%)", 1, 100],
  ["ask_after_days", "Ask “Did it nail it?” after (days)", 0, 90],
  ["answer_days", "Answers stay open for (days)", 1, 90],
  ["find_threshold", "“Make it easier to find” after (people)", 1, 10000],
];

/** When no operation matches confidently, nothing is guessed (review fixes 5). */
export const TOUCHES_TBD = "To be decided by Jeff";

type RowScore = { nailed_pct: number; top2_pct: number; margin: number; tier_label?: string | null } | null | undefined;
/** "88% Nailed it · 97% Nailed it or Okay · ± 4% · Excellent" (both numbers and the tier). */
export function scoreRowWords(score: RowScore): string {
  if (!score) return "No score yet";
  const parts = [`${score.nailed_pct}% Nailed it`, `${score.top2_pct}% Nailed it or Okay`, `± ${score.margin}%`];
  if (score.tier_label) parts.push(score.tier_label);
  return parts.join(" · ");
}
